/**
 * API utility with global auth handling.
 *
 * Protected requests should use authFetch so expired Live Rage access tokens
 * are refreshed in one place before the original request is retried.
 */

import {
    clearAllStorage,
    getAccessToken,
    getRefreshToken,
    saveAccessToken,
    saveRefreshToken,
} from './storage';
import { isEmbedded, notifyParentSessionExpired } from './embed';

let refreshPromise: Promise<string | null> | null = null;

export const getApiBaseUrl = (): string => {
    const apiUrl = process.env.REACT_APP_API_URL?.trim();
    if (!apiUrl) {
        throw new Error('REACT_APP_API_URL is not configured');
    }
    return apiUrl.replace(/\/$/, '');
};

const isFormDataBody = (body: BodyInit | null | undefined): boolean => {
    return typeof FormData !== 'undefined' && body instanceof FormData;
};

const makeRequestHeaders = (options: RequestInit = {}): Headers => {
    const headers = new Headers(options.headers || {});
    const token = getAccessToken();

    if (!headers.has('Content-Type') && !isFormDataBody(options.body as BodyInit | null | undefined)) {
        headers.set('Content-Type', 'application/json');
    }

    if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
};

export const refreshAccessToken = async (): Promise<string | null> => {
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = (async () => {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
            return null;
        }

        const currentToken = getAccessToken();
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };
        if (currentToken) {
            headers.Authorization = `Bearer ${currentToken}`;
        }

        const response = await fetch(getApiUrl('/v1/embed-auth/refresh'), {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
            return null;
        }

        const payload = await response.json().catch(() => null);
        const nextAccessToken = (
            payload?.accessToken
            || payload?.authToken
            || payload?.token
            || null
        );
        const nextRefreshToken = payload?.refreshToken || payload?.RefreshToken || null;

        if (nextAccessToken) {
            saveAccessToken(nextAccessToken);
        }
        // Rage rotates refresh tokens - persist the new one the backend now
        // returns, otherwise the next refresh would send the old (invalidated)
        // token and fail.
        if (nextRefreshToken) {
            saveRefreshToken(nextRefreshToken);
        }

        // Only report success if we actually got a NEW access token. Falling
        // back to the current (expired) token here would make authFetch retry
        // with a dead token, get another 401, and log the user out - turning a
        // refresh problem into a mysterious logout.
        return nextAccessToken || null;
    })().finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
};

/**
 * Handle unrecoverable auth errors.
 */
export const handleAuthError = (status: number): boolean => {
    if (status === 401) {
        console.warn('[Auth] Token expired or invalid.');
        clearAllStorage();
        if (isEmbedded()) {
            // Inside the Rage embed we must NOT drop the user on the dev
            // token-entry page. Tell the parent its session expired (Rage can
            // re-handoff a fresh token) and let LiveLoginPage show a graceful
            // "reconnecting" state instead of the paste-token form.
            notifyParentSessionExpired();
            window.location.href = '/live-login?embedded=true';
        } else {
            window.location.href = '/live-login';
        }
        return true;
    }
    return false;
};

/**
 * Wrapper around fetch that handles 401 globally
 */
export const authFetch = async (
    url: string,
    options: RequestInit = {},
    retryOnUnauthorized = true
): Promise<Response> => {
    const response = await fetch(url, {
        ...options,
        credentials: options.credentials || 'include',
        headers: makeRequestHeaders(options),
    });

    if (response.status !== 401 || !retryOnUnauthorized) {
        return response;
    }

    const refreshedToken = await refreshAccessToken().catch(() => null);
    if (!refreshedToken) {
        handleAuthError(401);
        return response;
    }

    const retryHeaders = makeRequestHeaders(options);
    retryHeaders.set('Authorization', `Bearer ${refreshedToken}`);
    const retryResponse = await fetch(url, {
        ...options,
        credentials: options.credentials || 'include',
        headers: retryHeaders,
    });

    if (retryResponse.status === 401) {
        handleAuthError(401);
    }

    return retryResponse;
};

/**
 * Get full API URL
 */
export const getApiUrl = (path: string): string => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${getApiBaseUrl()}${normalizedPath}`;
};
